import { ChangeDetectionStrategy, Component, OnInit, signal, TemplateRef, ViewChild, } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { View } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { UIContextAction } from 'angular-to-phaser';
import { firstText, Interaction, InteractionReply } from 'src/app/dto';
import { AuthService } from 'src/app/services/auth.service';
import { TutorialService } from 'src/app/services/tutorial.service';
import { UIStateService } from 'src/app/services/ui-state.service';
import { ShadowPopoverDirective } from 'src/app/directives/shadow-popover.directive';

@Component({
    selector: 'app-tutorial',
    templateUrl: './tutorial.component.html',
    styleUrls: ['./tutorial.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TutorialComponent extends View implements OnInit {

    @ViewChild('tutorial1') tutorialTemplate1: TemplateRef<any>;
    @ViewChild('tutorial1_5') tutorialTemplate1_5: TemplateRef<any>;
    @ViewChild('tutorial2') tutorialTemplate2: TemplateRef<any>;
    @ViewChild('tutorial3') tutorialTemplate3: TemplateRef<any>;
    @ViewChild('tutorial4') tutorialTemplate4: TemplateRef<any>;
    @ViewChild('tutorial5') tutorialTemplate5: TemplateRef<any>;

    constructor(private activeModal: NgbActiveModal, private modalService: NgbModal, private tutorialService: TutorialService, private router: Router, authService: AuthService, uiStateService: UIStateService) {
        super(authService, uiStateService);
        this.router.navigate([MainpageComponent.route]);
    }

    private activeTutorialStep: ShadowPopoverDirective;
    private player: Element;

    firstText = firstText;

    // Set inside collectWizardState(), called both synchronously (ngOnInit, the local
    // ChainedInteractionReply walk in chooseOption()) and from async subscribe callbacks
    // (chooseOption()'s submitTutorialChoice response) - the async path makes plain fields unsafe
    // for OnPush, so both are signals. currentTutorialStep/errorState aren't read by this
    // component's own template, so they stay plain fields.
    protected wizardAction = signal<Interaction>(undefined);
    errorState = false;
    currentTutorialStep = 0;

    protected options = signal<InteractionReply[]>([]);
    ngOnInit(): void {
        this.collectWizardState();
    }

    chooseOption(npcOption: InteractionReply): void {
        // A ChainedInteractionReply (e.g. the pass-through "Okay"/"Thanks!" hops between the
        // tutorial's end-of-flow Steps) is walked locally - it never reaches the server. Two of
        // these chained replies both carry IsFinalOption ("Okay" into GiftBasketPending, which
        // still has more to show, and "Thanks!" into the silent AllDone Step, which doesn't), so
        // that alone can't tell them apart - the authored signal is UIContextAction.Dismiss
        // (set on "Thanks!" and on the postponed-acknowledgement's own "Okay", which chains back
        // to the Welcome Step purely so CurrentStepId lands somewhere sane for the next greet -
        // not because that Welcome content should ever be shown here), the same enum value
        // interactor.component.ts's own handleInteractionChange already treats as "close this
        // interaction" for every other ChainedInteractionReply in the app: close directly,
        // without ever rendering the embedded NextEvent. Checked before the local render-walk
        // below (not after) so a dismissed reply's NextEvent content never paints, even for a
        // single frame, while the modal is closing.
        if (npcOption.Actions.find(a => a.NextEvent)) {
            if (npcOption.UIContextAction === UIContextAction.Dismiss) {
                this.tutorialService.resetTutorialState();
                this.closeModal();
                return;
            }
            this.tutorialService.advanceToSubsequentAction(npcOption);
            this.collectWizardState();
            return;
        }
        this.subscriptions.push(this.tutorialService.submitTutorialChoice(npcOption)
            .subscribe(
                (npcResponse) => {
                    this.collectWizardState();
                    // IsFinalOption is a pure display hint everywhere else in the app (see
                    // interactor.component.ts, which always submits regardless of it) - it must
                    // NOT skip submission, or replies like "Okay" (IsFinalOption true, but
                    // carrying the real item-grant effect) never reach the server at all. Submit
                    // first (above), then use IsFinalOption to decide whether this reply also
                    // ends the tutorial UI. The following "Thanks!" reply never reaches this
                    // branch at all - it's a pass-through chain (this function's own NextEvent
                    // check above catches it first and returns early), not a second submission.
                    //
                    // Deliberately does NOT close the modal here: the response we just collected
                    // (npcResponse.PlayerStateUpdate) is what tutorial.component.html's
                    // app-critter-state-update renders as the granted-item card + confetti lottie
                    // - closing immediately tore that down before it ever painted. Tutorial state
                    // is still reset immediately (harmless, invisible bookkeeping); the modal
                    // itself stays open until the user dismisses it via the modal's own close
                    // control, so they actually see what they got.
                    if (npcOption.DisplayMetadata.IsFinalOption) {
                        this.activeTutorialStep?.close();
                        this.tutorialService.resetTutorialState();
                        return;
                    }
                    // "Advance" is the only reply anywhere in this tutorial with ReturnsToEntry
                    // set - a reliable signal for "we've reached the InProgress placeholder,"
                    // unlike its DisplayMetadata (IsPrimaryOption:true, IsFinalOption:false),
                    // which "Complete the tutorial" shares exactly.
                    const isAdvanceOnlyResponse = npcResponse.Responses?.length === 1 && npcResponse.Responses[0].ReturnsToEntry;
                    if (npcOption.DisplayMetadata.IsPrimaryOption && isAdvanceOnlyResponse) {
                        this.startNextStep(0);
                        // Always reached with currentTutorialStep<5 - InProgress's own
                        // Prerequisite guarantees it - so the modal always closes here.
                        this.closeModal();
                    }
                }
            ));
    }

    openMenuNextStep(currentStep: number) {
        if (this.activeTutorialStep) { this.activeTutorialStep.close(); }
        setTimeout(() => this.uiStateService.show('main-sidebar-container'), 400);
        setTimeout(() => this.startNextStep(currentStep), 750);
    }

    openUserDetailsNextStep(currentStep: number) {
        if (this.activeTutorialStep) { this.activeTutorialStep.close(); }
        setTimeout(() => this.uiStateService.show('user-details-menu-toggle'), 600);
        this.openMenuNextStep(currentStep);
    }

    closeMenuNextStep(currentStep: number) {
        if (this.activeTutorialStep) { this.activeTutorialStep.close(); }
        this.uiStateService.hide('user-details-menu-toggle');
        this.uiStateService.hide('main-sidebar-container');
        this.startNextStep(currentStep);
    }

    startNextStep(currentStep: number) {
        if (this.activeTutorialStep) { this.activeTutorialStep.close(); }

        if (currentStep < 5) {
            if (currentStep !== 1.5 && currentStep !== 0 && currentStep > this.currentTutorialStep) { this.subscriptions.push(this.tutorialService.submitTutorialChoice(this.advanceReply()).subscribe(() => this.currentTutorialStep = this.tutorialService.currentTutorialStep)); }
            this.selectNextPopover();
        }
        if (currentStep >= 5) {
            this.subscriptions.push(this.tutorialService.submitTutorialChoice(this.advanceReply()).subscribe(() => {
                this.currentTutorialStep = this.tutorialService.currentTutorialStep;
                this.subscriptions.push(this.tutorialService.determineTutorialCompleteAndSetWizardState().subscribe(() => {
                    this.modalService.open(TutorialComponent); //show end screen
                }));
            }));
        }
    }

    // The declarative dialogue engine's "InProgress" Step always offers exactly one reply
    // ("Advance") while the tutorial is underway - looked up by ReturnsToEntry (the only reply
    // in this tutorial with it set) rather than a fixed action code, since replyIds are now
    // small, per-Step-scoped integers assigned by content, not a stable NPC-wide action code
    // scheme. See tutorial.service.ts's own comment.
    private advanceReply(): InteractionReply {
        return this.tutorialService.tutorialOptions?.find(r => r.ReturnsToEntry);
    }

    closeModal(): void {
        this.activeModal.close();
    }

    private collectWizardState() {
        this.options.set(this.tutorialService.tutorialOptions);
        this.wizardAction.set(this.tutorialService.wizardMessage);
        this.currentTutorialStep = this.tutorialService.currentTutorialStep;

    }

    private selectNextPopover() {
        const sortedElems = this.tutorialService.appShellTutorialSections.concat(this.tutorialService.tutorialSections).sort((a, b) => {
            const tutorialAOrder = this.determineOrder(a.appShadowPopover.toString());
            const tutorialBOrder = this.determineOrder(b.appShadowPopover.toString());
            return tutorialAOrder - tutorialBOrder;
        });
        const nextTutorialElem = sortedElems[0];
        if (!nextTutorialElem) {
            if (this.activeTutorialStep) {
                this.activeTutorialStep.close();
            }
            return;
        }

        this.tutorialService.removeTutorialSection(nextTutorialElem);

        let templateref = null;
        switch (nextTutorialElem.appShadowPopover) {
            case 'tutorial1': templateref = this.tutorialTemplate1; break;
            case 'tutorial1-5': templateref = this.tutorialTemplate1_5; break;
            case 'tutorial2': templateref = this.tutorialTemplate2; break;
            case 'tutorial3': templateref = this.tutorialTemplate3; break;
            case 'tutorial4': templateref = this.tutorialTemplate4; break;
            case 'tutorial5': templateref = this.tutorialTemplate5; break;

            default: templateref = null;
        }
        nextTutorialElem.autoClose = false;
        nextTutorialElem.appShadowPopover = templateref;
        if (this.activeTutorialStep) {
            this.activeTutorialStep.close();
        }

        this.activeTutorialStep = nextTutorialElem;
        nextTutorialElem.open();
    }

    public startAnimation() {
        this.player = document.querySelector('#confetti');
        (<any>this.player).play();
    };

    private determineOrder(tutorialName: string) {
        if (tutorialName) {
            return Number.parseFloat(tutorialName.replace('tutorial', '').replace('-', '.'));
        }
        return 666;
    }
}
